/**
 * CustomersDashboardPage — `/admin/customers/dashboard`.
 *
 * Reporting dashboard for the staff console "Customers" admin section. Pulls
 * the typed `customer.overview.v1` report from `service-reporting-api`
 * (`GET /v1/reports/customer-overview`).
 *
 * Layout:
 *   - top filter bar (date range default last 30d, grade, geoStateId)
 *   - 4-card KPI row (total, new in period, % WALK_IN, % REGISTERED)
 *   - 2 side-by-side top-customer tables (revenue + order count)
 *   - bottom horizontal-bar list of byState (top 10), pure flex divs (no chart lib)
 */
import { type JSX, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, Users } from 'lucide-react';

import { ApiError } from '../../../lib/api/client';
import { formatNgn } from '../../../lib/api/financial-services';
import {
  runCustomerOverviewReport,
  type CustomerOverviewReport,
  type CustomerOverviewTopRow,
  type RunCustomerOverviewParams,
} from '../../../lib/api/reports';
import { DeferredNotice, PageHeader } from '../../shared/StaffUI';

function isoDate(d: Date): string {
  // YYYY-MM-DD without timezone drift.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultRange(): { fromDate: string; toDate: string } {
  const today = new Date();
  const thirty = new Date(today);
  thirty.setDate(today.getDate() - 30);
  return { fromDate: isoDate(thirty), toDate: isoDate(today) };
}

function pct(numerator: number, denominator: number): string {
  if (denominator <= 0) return '0%';
  return `${Math.round((numerator * 100) / denominator)}%`;
}

interface KpiCardProps {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
}

function KpiCard({ label, value, hint }: KpiCardProps): JSX.Element {
  return (
    <div className="bg-white border border-border rounded-xl p-4">
      <p className="text-[10px] uppercase text-muted font-semibold">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {hint ? <p className="text-[10px] text-muted mt-1">{hint}</p> : null}
    </div>
  );
}

interface TopTableProps {
  readonly title: string;
  readonly rows: readonly CustomerOverviewTopRow[];
  readonly metricLabel: string;
  readonly metric: (row: CustomerOverviewTopRow) => string;
  readonly testId: string;
}

function TopTable({ title, rows, metricLabel, metric, testId }: TopTableProps): JSX.Element {
  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        <span className="text-[10px] uppercase text-muted">{metricLabel}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted p-4 text-center">No customers in this window.</p>
      ) : (
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border">
            {rows.map((r, idx) => (
              <tr key={r.customerId} className="hover:bg-gray-50">
                <td className="pl-4 pr-2 py-2 text-xs font-mono text-muted w-8">#{idx + 1}</td>
                <td className="px-2 py-2 text-xs">
                  <Link
                    to={`/admin/customers/${r.customerId}`}
                    className="font-medium text-gray-900 hover:underline"
                  >
                    {r.fullName}
                  </Link>
                  <p className="text-[10px] font-mono text-muted">{r.customerNumber}</p>
                </td>
                <td className="px-2 py-2 text-xs text-right text-muted">
                  {r.orderCount} {r.orderCount === 1 ? 'order' : 'orders'}
                </td>
                <td className="px-4 py-2 text-xs text-right font-semibold">{metric(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function CustomersDashboardPage(): JSX.Element {
  const initial = useMemo(() => defaultRange(), []);
  const [fromDate, setFromDate] = useState<string>(initial.fromDate);
  const [toDate, setToDate] = useState<string>(initial.toDate);
  const [grade, setGrade] = useState<string>('');
  const [geoStateRaw, setGeoStateRaw] = useState<string>('');

  const params: RunCustomerOverviewParams = useMemo(() => {
    const out: RunCustomerOverviewParams = { fromDate, toDate };
    if (grade) (out as { grade?: string }).grade = grade;
    if (geoStateRaw) {
      const n = Number(geoStateRaw);
      if (Number.isFinite(n)) (out as { geoStateId?: number }).geoStateId = n;
    }
    return out;
  }, [fromDate, toDate, grade, geoStateRaw]);

  const report = useQuery({
    queryKey: ['admin', 'customers', 'overview', params] as const,
    queryFn: () => runCustomerOverviewReport(params),
    retry: false,
    staleTime: 30_000,
  });

  const isMissing =
    report.isError &&
    report.error instanceof ApiError &&
    (report.error.status === 404 || report.error.status === 501);

  const data: CustomerOverviewReport | undefined = report.data;

  // Top 10 states for the bar list.
  const topStates = useMemo(() => {
    if (!data) return [];
    const sorted = [...data.byState].sort((a, b) => b.count - a.count).slice(0, 10);
    const max = sorted[0]?.count ?? 1;
    return sorted.map((s) => ({ ...s, pctOfMax: max > 0 ? (s.count * 100) / max : 0 }));
  }, [data]);

  return (
    <div className="space-y-6">
      <Link
        to="/admin/customers"
        className="inline-flex items-center gap-1 text-xs text-muted hover:text-gray-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Customers
      </Link>

      <PageHeader
        title="Customer reporting"
        description="Aggregate customer growth, segmentation, and revenue concentration. Powered by customer.overview.v1."
        actions={
          <div className="flex items-center gap-2 text-xs text-muted">
            <BarChart3 className="w-4 h-4" />
            customer.overview.v1
          </div>
        }
      />

      {/* Filter bar */}
      <div className="bg-white border border-border rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label htmlFor="rep-from" className="text-[10px] font-semibold uppercase text-muted">
            From
          </label>
          <input
            id="rep-from"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            data-testid="rep-from"
          />
        </div>
        <div>
          <label htmlFor="rep-to" className="text-[10px] font-semibold uppercase text-muted">
            To
          </label>
          <input
            id="rep-to"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            data-testid="rep-to"
          />
        </div>
        <div>
          <label htmlFor="rep-grade" className="text-[10px] font-semibold uppercase text-muted">
            Grade
          </label>
          <select
            id="rep-grade"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg bg-white"
          >
            <option value="">All grades</option>
            <option value="WALK_IN">Walk-in</option>
            <option value="REGISTERED">Registered</option>
          </select>
        </div>
        <div>
          <label htmlFor="rep-state" className="text-[10px] font-semibold uppercase text-muted">
            State (geoStateId)
          </label>
          <input
            id="rep-state"
            type="number"
            min={1}
            value={geoStateRaw}
            onChange={(e) => setGeoStateRaw(e.target.value)}
            className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
          />
        </div>
      </div>

      {report.isPending ? (
        <p className="text-sm text-muted">Loading report…</p>
      ) : isMissing ? (
        <DeferredNotice title="Endpoint not yet deployed">
          <p>
            <code className="font-mono">GET /v1/reports/customer-overview</code> is
            not yet available — check back once service-reporting-api ships.
          </p>
        </DeferredNotice>
      ) : report.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          <p className="font-semibold">Reporting service unreachable</p>
          <p>{(report.error as Error).message}</p>
        </div>
      ) : !data ? null : (
        <>
          {data.stub ? (
            <div
              className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"
              data-testid="stub-warning"
            >
              <p className="font-semibold">Upstream data partially available</p>
              <p>
                Some figures fell back to placeholder zeros — the upstream
                orders/revenue surface is still warming up.
              </p>
            </div>
          ) : null}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-row">
            <KpiCard label="Total customers" value={data.totalCustomers.toLocaleString('en-NG')} />
            <KpiCard label="New in period" value={data.newInPeriod.toLocaleString('en-NG')} />
            <KpiCard
              label="% Walk-in"
              value={pct(data.byGrade.WALK_IN, data.totalCustomers)}
              hint={`${data.byGrade.WALK_IN.toLocaleString('en-NG')} customers`}
            />
            <KpiCard
              label="% Registered"
              value={pct(data.byGrade.REGISTERED, data.totalCustomers)}
              hint={`${data.byGrade.REGISTERED.toLocaleString('en-NG')} customers`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TopTable
              title="Top by revenue"
              rows={data.topByRevenue}
              metricLabel="₦ revenue"
              metric={(r) => formatNgn(r.totalNgn ?? '0')}
              testId="top-revenue"
            />
            <TopTable
              title="Top by order count"
              rows={data.topByOrders}
              metricLabel="Orders"
              metric={(r) => r.orderCount.toLocaleString('en-NG')}
              testId="top-orders"
            />
          </div>

          <div className="bg-white border border-border rounded-xl p-4" data-testid="bystate-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-4 h-4 text-muted" />
                Top 10 states by customer count
              </h2>
              <span className="text-[10px] uppercase text-muted">
                {data.byState.length} state{data.byState.length === 1 ? '' : 's'} total
              </span>
            </div>
            {topStates.length === 0 ? (
              <p className="text-sm text-muted text-center py-4">
                No state-level data in this window.
              </p>
            ) : (
              <ul className="space-y-2">
                {topStates.map((s) => (
                  <li key={`${s.stateId ?? 'null'}-${s.stateName ?? '-'}`} className="text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">{s.stateName ?? 'Unknown'}</span>
                      <span className="text-muted">{s.count.toLocaleString('en-NG')}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-nipost-green rounded-full"
                        style={{ width: `${Math.max(2, s.pctOfMax)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-[10px] text-muted text-right">
            Generated at {new Date(data.generatedAt).toLocaleString('en-NG')}
          </p>
        </>
      )}
    </div>
  );
}
